import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@material-ui/core";
import api from "../../services/api";

const LINHAS_POR_FOLHA = 32;

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const linhasComVazias = (folha) =>
  Array.from({ length: LINHAS_POR_FOLHA }, (_, i) => folha.linhas[i] || null);

const folhaHtml = (folha) => {
  const corpo = linhasComVazias(folha)
    .map((p) =>
      p
        ? `<div class="row${p.manual ? " manual" : ""}"><span>${esc(p.dia)}</span><span>${esc(p.nome)}</span><span class="v">${esc(p.valor)}</span><span>${esc(p.hora)}</span></div>`
        : `<div class="row"><span></span><span></span><span></span><span></span></div>`
    )
    .join("\n");
  const selo = folha.impressa ? `<div class="selo">FOLHA JÁ IMPRESSA</div>` : "";
  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>Anotação de Pix - Folha ${folha.numero}</title>
<style>
@page { size: A4; margin: 0; }
html, body { margin: 0; background: #fff; }
body { font-family: Arial, Helvetica, sans-serif; color: #111; }
.folha { width: 210mm; height: 297mm; box-sizing: border-box; padding: 12mm 14mm 10mm 14mm; position: relative; overflow: hidden; }
.titulo { height: 8mm; line-height: 8mm; font-size: 11pt; font-weight: 700; margin-left: 12mm; }
.cab, .row { display: grid; grid-template-columns: 22mm 1fr 30mm 18mm; height: 8mm; line-height: 8mm; margin-left: 12mm; border-bottom: 0.3mm solid #8fb3dd; font-size: 9pt; white-space: nowrap; overflow: hidden; }
.cab { font-weight: 700; border-bottom: 0.6mm solid #3d6fa8; text-transform: uppercase; }
.row span { overflow: hidden; text-overflow: ellipsis; padding-left: 2mm; }
.row .v { text-align: right; padding-right: 3mm; font-variant-numeric: tabular-nums; }
.folha::before { content: ""; position: absolute; top: 0; bottom: 0; left: 18mm; width: 0.4mm; background: #d9534f; }
.row.manual { color: #b91c1c; font-weight: 700; }
.selo { position: absolute; top: 18mm; right: 14mm; border: 1.2mm solid #b91c1c; color: #b91c1c; font: 700 14pt Arial, sans-serif; padding: 2mm 4mm; transform: rotate(-8deg); letter-spacing: 0.5mm; }
</style></head>
<body><div class="folha">
<div class="titulo">Anotação de Pix - Folha ${folha.numero}</div>
<div class="cab"><span>Data</span><span>Nome - cidade</span><span style="text-align:right;padding-right:3mm">Valor</span><span style="padding-left:2mm">Hora</span></div>
${corpo}
${selo}
</div>
<script>
window.addEventListener('afterprint', function () {
  if (window.opener) window.opener.postMessage({ type: 'pixsheet-afterprint', numero: ${folha.numero} }, '*');
});
window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 200); });
</script>
</body></html>`;
};

const LINHA_MANUAL_VAZIA = { dataTxt: "", nome: "", valor: "", recolhidoPor: "" };

const PixSheetModal = ({ open, onClose, onChanged }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [confirmar, setConfirmar] = useState(null);
  const [indice, setIndice] = useState(null);
  const [linhaManual, setLinhaManual] = useState(LINHA_MANUAL_VAZIA);
  const [salvandoManual, setSalvandoManual] = useState(false);
  const timerManualRef = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/pix-sheets");
      setDados(data);
      setIndice((atual) => {
        const ultima = Math.max(0, data.folhas.length - 1);
        return atual === null ? ultima : Math.min(atual, ultima);
      });
      setErro("");
      if (onChanged) onChanged(data);
    } catch (_error) {
      setErro("Não deu pra carregar as folhas de Pix agora.");
    }
  }, [onChanged]);

  useEffect(() => {
    if (open) carregar();
  }, [open, carregar]);

  useEffect(() => {
    const aoVoltarDaImpressao = (event) => {
      if (event?.data?.type !== "pixsheet-afterprint") return;
      setConfirmar({ numero: event.data.numero });
    };
    window.addEventListener("message", aoVoltarDaImpressao);
    return () => window.removeEventListener("message", aoVoltarDaImpressao);
  }, []);

  const imprimir = (folha) => {
    const janela = window.open("", "_blank");
    if (!janela) {
      setErro("O navegador bloqueou a janela de impressão. Libere pop-ups deste site.");
      return;
    }
    janela.document.open();
    janela.document.write(folhaHtml(folha));
    janela.document.close();
  };

  const salvarLinhaManual = useCallback(
    async (linha) => {
      if (!linha.nome.trim() || !linha.valor.trim()) return;
      setSalvandoManual(true);
      try {
        const { data } = await api.post("/pix-sheets/manual", linha);
        setDados(data);
        setIndice(Math.max(0, data.folhas.length - 1));
        setLinhaManual(LINHA_MANUAL_VAZIA);
        if (onChanged) onChanged(data);
      } catch (_error) {
        setErro("Não deu pra salvar essa anotação agora. Confira os dados e tente de novo.");
      } finally {
        setSalvandoManual(false);
      }
    },
    [onChanged]
  );

  const handleCampoManual = (campo, valor) => {
    const proxima = { ...linhaManual, [campo]: valor };
    setLinhaManual(proxima);
    if (timerManualRef.current) clearTimeout(timerManualRef.current);
    timerManualRef.current = setTimeout(() => salvarLinhaManual(proxima), 1800);
  };

  const confirmarImpressao = async () => {
    if (!confirmar) return;
    try {
      const { data } = await api.post("/pix-sheets/printed", { numero: confirmar.numero });
      setDados(data);
      if (onChanged) onChanged(data);
    } catch (_error) {
      setErro("Não deu pra marcar a folha como impressa. Tente de novo.");
    } finally {
      setConfirmar(null);
    }
  };

  const folhas = dados?.folhas || [];
  const atual = indice === null ? folhas.length - 1 : Math.min(indice, folhas.length - 1);
  const folha = folhas[atual];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Folhas de Pix</DialogTitle>
        <DialogContent dividers>
          {erro && <Typography color="error">{erro}</Typography>}
          {dados && !folha && <Typography>Nenhum Pix recebido ainda.</Typography>}
          {folha && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={atual <= 0}
                  onClick={() => setIndice(Math.max(0, atual - 1))}
                  aria-label="folha anterior"
                >
                  {"<"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={atual >= folhas.length - 1}
                  onClick={() => setIndice(Math.min(folhas.length - 1, atual + 1))}
                  aria-label="próxima folha"
                >
                  {">"}
                </Button>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  Folha {folha.numero} de {folhas.length} ({folha.linhas.length}/{LINHAS_POR_FOLHA} Pix)
                </Typography>
                {folha.impressa && (
                  <Typography variant="body2" style={{ color: "#b91c1c", fontWeight: 700 }}>
                    FOLHA JÁ IMPRESSA
                  </Typography>
                )}
                <Button size="small" variant="outlined" onClick={() => imprimir(folha)}>
                  Imprimir
                </Button>
              </div>
              <div
                style={{
                  width: 794,
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #ccc",
                  background: "#fff",
                  color: "#111",
                  padding: "12px 14px",
                  fontFamily: "Arial, sans-serif",
                  position: "relative",
                  fontSize: 12,
                }}
              >
                {(() => {
                  const ehUltimaFolha = atual === folhas.length - 1;
                  const indiceEditavel = ehUltimaFolha && !folha.impressa ? folha.linhas.length : -1;
                  return linhasComVazias(folha).map((p, i) => {
                    if (!p && i === indiceEditavel) {
                      return (
                        <div
                          key="linha-manual-editavel"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "60px 1fr 110px 60px",
                            borderBottom: "1px solid #8fb3dd",
                            height: 26,
                            alignItems: "center",
                          }}
                        >
                          <input
                            value={linhaManual.dataTxt}
                            onChange={(e) => handleCampoManual("dataTxt", e.target.value)}
                            placeholder="dd/mm"
                            disabled={salvandoManual}
                            style={{ border: "none", outline: "none", color: "#b91c1c", fontWeight: 700, width: "100%", fontSize: 12 }}
                          />
                          <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                            <input
                              value={linhaManual.nome}
                              onChange={(e) => handleCampoManual("nome", e.target.value)}
                              placeholder="Nome do cliente"
                              disabled={salvandoManual}
                              style={{ border: "none", outline: "none", color: "#b91c1c", fontWeight: 700, flex: 1, minWidth: 0, fontSize: 12 }}
                            />
                            <span style={{ color: "#b91c1c", fontSize: 11, fontStyle: "italic", whiteSpace: "nowrap" }}>
                              (
                              <input
                                value={linhaManual.recolhidoPor}
                                onChange={(e) => handleCampoManual("recolhidoPor", e.target.value)}
                                placeholder="quem"
                                disabled={salvandoManual}
                                style={{ border: "none", outline: "none", color: "#b91c1c", width: 70, fontSize: 11, fontStyle: "italic" }}
                              />
                              {" Recolheu )"}
                            </span>
                          </div>
                          <input
                            value={linhaManual.valor}
                            onChange={(e) => handleCampoManual("valor", e.target.value)}
                            placeholder="R$ 0,00"
                            disabled={salvandoManual}
                            style={{ border: "none", outline: "none", color: "#b91c1c", fontWeight: 700, textAlign: "right", width: "100%", fontSize: 12 }}
                          />
                          <span />
                        </div>
                      );
                    }
                    return (
                      <div
                        key={p ? p.tx : `vazia-${i}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "60px 1fr 110px 60px",
                          borderBottom: "1px solid #8fb3dd",
                          lineHeight: "26px",
                          height: 26,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          color: p?.manual ? "#b91c1c" : "inherit",
                          fontWeight: p?.manual ? 700 : 400,
                        }}
                      >
                        <span>{p ? p.dia : ""}</span>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{p ? p.nome : ""}</span>
                        <span style={{ textAlign: "right" }}>{p ? p.valor : ""}</span>
                        <span style={{ paddingLeft: 8 }}>{p ? p.hora : ""}</span>
                      </div>
                    );
                  });
                })()}
                {folha.impressa && (
                  <div
                    style={{
                      position: "absolute",
                      top: 18,
                      right: 18,
                      border: "3px solid #b91c1c",
                      color: "#b91c1c",
                      fontWeight: 700,
                      padding: "4px 10px",
                      transform: "rotate(-8deg)",
                      background: "rgba(255,255,255,0.85)",
                    }}
                  >
                    FOLHA JÁ IMPRESSA
                  </div>
                )}
              </div>
              {atual === folhas.length - 1 && !folha.impressa && (
                <Typography variant="caption" style={{ display: "block", marginTop: 6, color: "#667781" }}>
                  {salvandoManual
                    ? "Salvando…"
                    : "Escreva na linha em vermelho para anotar um valor recolhido em mãos (preencha nome e valor)."}
                </Typography>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmar)} onClose={() => setConfirmar(null)}>
        <DialogTitle>A folha saiu da impressora?</DialogTitle>
        <DialogContent>
          <Typography>
            Só marco a folha como impressa se ela realmente foi impressa. Se você cancelou, responda Não.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)}>Não</Button>
          <Button color="primary" variant="contained" onClick={confirmarImpressao}>
            Sim, foi impressa
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PixSheetModal;
